import { useEffect, useState } from "react";
import { Trash2, UserPlus, Mail, ShieldCheck, Send } from "lucide-react";
import { Header } from "@/components/Header";
import { apiFetch, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [digest, setDigest] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [recap, setRecap] = useState(null);
  const [newsletterCount, setNewsletterCount] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [communities, setCommunities] = useState([]);

  const loadCommunities = () =>
    apiFetch("/communities").then((r) => setCommunities(r.communities)).catch(() => {});

  const saveLead = async (community, changes) => {
    try {
      await apiFetch("/admin/communities", {
        method: "PUT",
        body: JSON.stringify({ slug: community.slug, ...changes }),
      });
      toast.success(`${community.name} host updated`);
      loadCommunities();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadSponsors = () => apiFetch("/public/sponsors").then((r) => setSponsors(r.sponsors)).catch(() => {});

  const resetSponsor = async (sponsor) => {
    try {
      await apiFetch(`/admin/sponsors?name=${encodeURIComponent(sponsor.name)}`, { method: "DELETE" });
      toast.success(`${sponsor.name} back to automatic tier`);
      loadSponsors();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveSponsor = async (sponsor, changes) => {
    try {
      await apiFetch("/admin/sponsors", {
        method: "PUT",
        body: JSON.stringify({
          name: sponsor.name,
          tier: changes.tier ?? sponsor.tier,
          blurb: changes.blurb ?? sponsor.blurb,
          logo_url: sponsor.logo_url || "",
          website: sponsor.website || "",
        }),
      });
      toast.success(`${sponsor.name} updated`);
      loadSponsors();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadNewsletter = async () => {
    try {
      const res = await apiFetch("/admin/newsletter");
      setNewsletterCount(res.count);
      toast.success(`${res.count} member email(s) on the list`);
    } catch (err) {
      toast.error(err.message);
    }
  };
  const [sending, setSending] = useState(false);

  const load = () => apiFetch("/admin/leaders").then(setData).catch(() => setData(null));

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await apiFetch("/admin/leaders", { method: "POST", body: JSON.stringify({ email, note }) });
      toast.success(`${email} can now publish listings`);
      setEmail("");
      setNote("");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (e) => {
    try {
      await apiFetch(`/admin/leaders/${encodeURIComponent(e)}`, { method: "DELETE" });
      toast.success("Leader access removed");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const runReminders = async (kind) => {
    try {
      const res = await apiFetch(`/reminders/run?kind=${kind}`, { method: "POST" });
      setReminders(res);
      toast.success(`${res.sent} reminder(s) sent for ${res.events} event(s)`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const runRecap = async () => {
    try {
      const res = await apiFetch("/recap/run", { method: "POST" });
      setRecap(res);
      if (res.sent === 0 && res.failed > 0) {
        toast.error(`Nothing delivered — ${res.failures?.[0]?.error || "email provider rejected the send"}`);
      } else {
        toast.success(`Recap sent to ${res.sent} host(s)${res.failed ? `, ${res.failed} failed` : ""}`);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const preview = async () => {    try {
      setDigest(await apiFetch("/digest/preview"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await apiFetch("/digest/send", { method: "POST" });
      if (res.sent === 0 && res.failed > 0) {
        toast.error(`Nothing delivered — ${res.failures?.[0]?.error || "email provider rejected the send"}`);
      } else {
        toast.success(`Digest sent to ${res.sent} member(s)${res.failed ? `, ${res.failed} failed` : ""}`);
      }
      preview();
    } catch (err) {
      toast.error(err.message);
    }
    setSending(false);
  };

  if (loading) return <div className="min-h-screen bg-white" />;

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900">Super admins only</h1>
          <p className="mt-3 text-neutral-600" data-testid="admin-denied">
            Only the calendar's super admins can manage who publishes listings.
          </p>
          {!user && (
            <button
              onClick={login}
              data-testid="admin-login"
              className="mt-6 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-700"
            >
              Continue with Google
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Admin</h1>
        <p className="mt-2 text-sm text-neutral-500">Signed in as {user.email} · super admin</p>

        <section className="mt-10">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Community leaders</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Anyone on this list can publish and edit listings after signing in with Google.
          </p>

          <form onSubmit={add} className="mt-5 flex flex-col gap-3 sm:flex-row" data-testid="add-leader-form">
            <input
              required
              type="email"
              data-testid="leader-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="leader@example.com"
              className="flex-1 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
            />
            <input
              data-testid="leader-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Community / note"
              className="flex-1 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
            />
            <button
              data-testid="add-leader-btn"
              className="flex items-center justify-center gap-2 rounded-full bg-[#B8FF2E] px-5 py-2.5 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
            >
              <UserPlus className="h-4 w-4" /> Invite
            </button>
          </form>

          <ul className="mt-6 divide-y divide-neutral-200" data-testid="leaders-list">
            {(data?.super_admins || []).map((e) => (
              <li key={e} className="flex items-center gap-3 py-3">
                <ShieldCheck className="h-4 w-4 text-[#4D7C0F]" />
                <span className="flex-1 text-sm font-semibold text-neutral-900">{e}</span>
                <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-neutral-600">
                  super admin
                </span>
              </li>
            ))}
            {(data?.leaders || []).map((l) => (
              <li key={l.email} className="flex items-center gap-3 py-3" data-testid={`leader-row-${l.email}`}>
                <Mail className="h-4 w-4 text-neutral-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-neutral-900">{l.email}</p>
                  {l.note && <p className="text-xs text-neutral-500">{l.note}</p>}
                </div>
                <button
                  data-testid={`remove-leader-${l.email}`}
                  onClick={() => remove(l.email)}
                  className="grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 hover:bg-neutral-100"
                >
                  <Trash2 className="h-4 w-4 text-[#4D7C0F]" />
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 border-t border-neutral-200 pt-10">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Weekly digest</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Sent automatically every Monday at 8:00 AM Pacific to everyone with an account.
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={preview}
              data-testid="digest-preview-btn"
              className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
            >
              Preview this week's digest
            </button>
            <button
              onClick={sendNow}
              disabled={sending}
              data-testid="digest-send-btn"
              className="flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-neutral-700 disabled:opacity-60"
            >
              <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send now"}
            </button>
          </div>

          {digest && (
            <div className="mt-6" data-testid="digest-preview">
              <p className="text-sm font-semibold text-neutral-700">
                {digest.event_count} event(s) this week · {digest.recipient_count} recipient(s)
              </p>
              <div className="mt-3 overflow-hidden rounded-2xl border border-neutral-200">
                <iframe title="Digest preview" srcDoc={digest.html} className="h-[520px] w-full" style={{ border: 0 }} />
              </div>
              {digest.history?.length > 0 && (
                <ul className="mt-4 space-y-1 text-xs text-neutral-500" data-testid="digest-history">
                  {digest.history.map((h, i) => (
                    <li key={i}>
                      {new Date(h.sent_at).toLocaleString()} · {h.sent} sent, {h.failed} failed · by {h.triggered_by}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        <section className="mt-14 border-t border-neutral-200 pt-10">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Monthly recap</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Emailed to every community leader and admin on the 1st at 9:30 AM Pacific — RSVPs, check-ins, ratings and photos from the month just gone.
          </p>
          <button
            onClick={runRecap}
            data-testid="recap-send-btn"
            className="mt-5 flex items-center gap-2 rounded-full bg-[#B8FF2E] px-5 py-2.5 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
          >
            <Send className="h-4 w-4" /> Send the recap now
          </button>
          {recap && (
            <p
              className={`mt-4 text-sm font-semibold ${recap.sent === 0 && recap.failed > 0 ? "text-red-600" : "text-neutral-700"}`}
              data-testid="recap-result"
            >
              {recap.events} event(s) · {recap.rsvps} RSVPs · {recap.attended} checked in · {recap.ratings} ratings ·{" "}
              {recap.photos} photos · sent to {recap.sent} host(s)
              {recap.failed ? ` · ${recap.failed} failed (${recap.failures?.[0]?.error || "email provider rejected the send"})` : ""}
            </p>
          )}
        </section>

        <section className="mt-14 border-t border-neutral-200 pt-10">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Sponsor tiers</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Tiers default to event volume. Set a tier and blurb to override what shows on the public page.
          </p>
          <button
            onClick={loadSponsors}
            data-testid="load-sponsors-btn"
            className="mt-4 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
          >
            {sponsors.length ? "Refresh sponsors" : "Load sponsors"}
          </button>

          {sponsors.length > 0 && (
            <ul className="mt-5 divide-y divide-neutral-200" data-testid="sponsor-tier-list">
              {sponsors.slice(0, 30).map((s) => (
                <li key={s.name} className="flex flex-wrap items-center gap-3 py-3" data-testid={`tier-row-${s.name}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-900">{s.name}</p>
                    <p className="text-xs text-neutral-500">
                      {s.event_count} events{s.tier_set_by_admin ? " · tier set manually" : " · auto tier"}
                    </p>
                  </div>
                  <select
                    data-testid={`tier-select-${s.name}`}
                    value={s.tier}
                    onChange={(e) => saveSponsor(s, { tier: e.target.value })}
                    className="rounded-lg border border-neutral-300 px-3 py-2 text-sm font-semibold"
                  >
                    {["Presenting", "Supporting", "Community"].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <input
                    data-testid={`tier-blurb-${s.name}`}
                    defaultValue={s.blurb}
                    onBlur={(e) => e.target.value !== s.blurb && saveSponsor(s, { blurb: e.target.value })}
                    placeholder="Short blurb for the front page"
                    className="min-w-[220px] flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
                  />
                  {s.tier_set_by_admin && (
                    <button
                      onClick={() => resetSponsor(s)}
                      data-testid={`tier-reset-${s.name}`}
                      className="rounded-full border border-neutral-300 px-3 py-2 text-xs font-semibold text-neutral-700 transition-colors duration-200 hover:bg-neutral-50"
                    >
                      Reset to auto
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-14 border-t border-neutral-200 pt-10">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Community hosts</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Name the real leader for each community — their name and contact show on the community page.
          </p>
          <button
            onClick={loadCommunities}
            data-testid="load-communities-btn"
            className="mt-4 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
          >
            {communities.length ? "Refresh communities" : "Load communities"}
          </button>

          {communities.length > 0 && (
            <ul className="mt-5 divide-y divide-neutral-200" data-testid="community-lead-list">
              {communities.map((c) => (
                <li key={c.slug} className="flex flex-wrap items-center gap-3 py-3" data-testid={`lead-row-${c.slug}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-900">{c.name}</p>
                    <p className="text-xs text-neutral-500">{c.upcoming} upcoming · {c.follower_count} following</p>
                  </div>
                  <input
                    data-testid={`lead-name-${c.slug}`}
                    defaultValue={c.lead_name}
                    onBlur={(e) => e.target.value !== c.lead_name && saveLead(c, { leader_name: e.target.value })}
                    placeholder="Host name"
                    className="w-40 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
                  />
                  <input
                    data-testid={`lead-title-${c.slug}`}
                    defaultValue={c.lead_title}
                    onBlur={(e) => e.target.value !== c.lead_title && saveLead(c, { leader_title: e.target.value })}
                    placeholder="Title"
                    className="w-40 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
                  />
                  <input
                    data-testid={`lead-email-${c.slug}`}
                    defaultValue={c.lead_email}
                    onBlur={(e) => e.target.value !== c.lead_email && saveLead(c, { leader_email: e.target.value })}
                    placeholder="Email or link"
                    className="w-48 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-14 border-t border-neutral-200 pt-10">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Newsletter list</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Every member email collected at sign-in{newsletterCount !== null ? ` · ${newsletterCount} on the list` : ""}.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={loadNewsletter}
              data-testid="newsletter-count-btn"
              className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
            >
              Refresh count
            </button>
            <a
              href={`${API}/admin/newsletter?fmt=csv`}
              data-testid="newsletter-csv-link"
              className="rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-neutral-700"
            >
              Download CSV
            </a>
          </div>
        </section>

        <section className="mt-14 border-t border-neutral-200 pt-10">
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">RSVP reminders</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Anyone who RSVPs gets an email 24 hours before and again at 8:15 AM Pacific on the day. Run a pass manually
            if you need to.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={() => runReminders("morning")}
              data-testid="reminders-morning-btn"
              className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
            >
              Send day-of reminders
            </button>
            <button
              onClick={() => runReminders("24h")}
              data-testid="reminders-24h-btn"
              className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
            >
              Send 24-hour reminders
            </button>
          </div>
          {reminders && (
            <p className="mt-4 text-sm text-neutral-600" data-testid="reminders-result">
              {reminders.kind === "morning" ? "Day-of" : "24-hour"} pass · {reminders.events} event(s) in window ·{" "}
              {reminders.sent} email(s) sent
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
